import React, { useState, useRef, useEffect } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import {
  Book,
  Calendar,
  Document,
  Actions,
  Eye,
  Download,
} from "@/components/icons";
import Button from "@/components/ui/button";
import type { ClientCertificate } from "../../types/client-certificates.types";
import styles from "./CertificateCard.module.scss";

export interface CertificateCardProps {
  certificate: ClientCertificate;
  onActionClick?: (certificate: ClientCertificate, action: string) => void;
  className?: string;
  /** Başlığın yanında gösterilecek tür rozeti (ör. "Stajyer"). */
  badgeLabel?: string;
  /** Menüye eklenecek ek aksiyonlar (ör. admin tarafında "Düzenle"). */
  extraActions?: { id: string; label: string; Icon: React.FC<{ width?: number; height?: number; className?: string }> }[];
}

const DEFAULT_ACTIONS = [
  {
    id: "view",
    labelKey: "clientCertificates.card.view",
    defaultLabel: i18n.t("clientCertificateCard.view"),
    Icon: Eye,
  },
  {
    id: "download",
    labelKey: "clientCertificates.card.download",
    defaultLabel: i18n.t("clientCertificateCard.download"),
    Icon: Download,
  },
];

const CertificateCard: React.FC<CertificateCardProps> = ({
  certificate,
  onActionClick,
  className = "",
  badgeLabel,
  extraActions = [],
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`${styles.card} ${className}`}>
      <div className={styles.card__imageWrap}>
        {certificate.rawHtml ? (
          <div
            className={styles.card__rawHtml}
            dangerouslySetInnerHTML={{ __html: certificate.rawHtml }}
          />
        ) : certificate.imageUrl ? (
          <img
            src={certificate.imageUrl}
            alt=""
            className={styles.card__image}
          />
        ) : (
          <div className={styles.card__placeholder}>
            <span className={styles.card__placeholderTitle}>
              CERTIFICATE OF APPRECIATION
            </span>
            <span className={styles.card__placeholderSub}>
              THIS CERTIFICATE IS PROUDLY PRESENTED TO
            </span>
            <span className={styles.card__placeholderName}>Mariah Doe</span>
            <span className={styles.card__placeholderMeta}>
              DATE · SIGNATURE
            </span>
          </div>
        )}
      </div>
      <div className={styles.card__body}>
        <div className={styles.card__header}>
          <div className={styles.card__titleWrap}>
            <h3 className={styles.card__title}>{certificate.title}</h3>
            {badgeLabel && (
              <span className={styles.card__badge}>{badgeLabel}</span>
            )}
          </div>
          <div className={styles.card__actions} ref={menuRef}>
            <Button
              variant="text"
              size="small"
              onClick={() => setMenuOpen((o) => !o)}
              className={styles.card__menuTrigger}
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              <Actions width={20} height={20} />
            </Button>
            {menuOpen && (
              <ul className={styles.card__menu}>
                {[
                  ...DEFAULT_ACTIONS.map((a) => ({
                    id: a.id,
                    label: t(a.labelKey, a.defaultLabel),
                    Icon: a.Icon,
                  })),
                  ...extraActions,
                ].map((a) => {
                  const Icon = a.Icon;
                  return (
                    <li key={a.id}>
                      <button
                        type="button"
                        className={styles.card__menuItem}
                        onClick={() => {
                          onActionClick?.(certificate, a.id);
                          setMenuOpen(false);
                        }}
                      >
                        <Icon
                          width={18}
                          height={18}
                          className={styles.card__menuIcon}
                        />
                        {a.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        <ul className={styles.card__details}>
          <li className={styles.card__detail}>
            <Book
              width={20}
              height={20}
              className={styles.card__detailIcon}
              color="#14181f"
            />
            <span className={styles.card__detailLabel}>
              {t("clientCertificates.card.relatedTraining")}
            </span>
            <span className={styles.card__detailValue}>
              {certificate.relatedTraining}
            </span>
          </li>
          <li className={styles.card__detail}>
            <Calendar
              width={20}
              height={20}
              className={styles.card__detailIcon}
            />
            <span className={styles.card__detailLabel}>
              {t("clientCertificates.card.issueDate")}
            </span>
            <span className={styles.card__detailValue}>
              {certificate.issueDate}
            </span>
          </li>
          <li className={styles.card__detail}>
            <Document
              width={20}
              height={20}
              className={styles.card__detailIcon}
            />
            <span className={styles.card__detailLabel}>
              {t("clientCertificates.card.certificateNo")}
            </span>
            <span className={styles.card__detailValue}>
              {certificate.certificateNo}
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CertificateCard;
